import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Typography,
  Box,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip
} from '@mui/material'
import {
  ViewModule as ViewModuleIcon,
  TableRows as TableRowsIcon
} from '@mui/icons-material'

import CampaignCardList from './CampaignCardList'
import CampaignTableList from './CampaignTableList'
import DebouncedInput from '../../mailTemplate/src/components/atomic/atoms/DebouncedInput'

const VIEW_STORAGE_KEY = 'mc:campaigns:viewMode'

const filterAndSortCampaigns = (campaigns, searchTerm, sortBy) => {
  let filtered = campaigns

  if (searchTerm) {
    const search = String(searchTerm).toLowerCase()
    filtered = filtered.filter(campaign =>
      Object.values({
        currentCountryCode: campaign.currentCountryCode,
        importerCountryCode: campaign.importerCountryCode,
        hsCodes: campaign.hsCodes,
        name: campaign.name,
        productName: campaign.productName
      }).some(value =>
        String(value).toLowerCase().includes(search)
      )
    )
  }

  if (sortBy) {
    filtered = [...filtered].sort((a, b) => {
      const dateA = new Date(a.createdAt)
      const dateB = new Date(b.createdAt)

      if (sortBy === 'newest') {
        return dateB - dateA
      } else if (sortBy === 'oldest') {
        return dateA - dateB
      }
      return 0
    })
  }

  return filtered
}

const SORT_OPTIONS = [
  { value: 'newest', label: 'mailTemplate.campaigns.sort.newest' },
  { value: 'oldest', label: 'mailTemplate.campaigns.sort.oldest' }
]

const CampaignFilters = ({ searchTerm, onSearchChange, sortBy, onSortChange, viewMode, onViewModeChange, t }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' }, alignItems: 'flex-end' }}>
    <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' }, alignItems: 'flex-end' }}>
      <DebouncedInput
        value={searchTerm || ''}
        onChange={onSearchChange}
        placeholder={t('mailTemplate.campaigns.search')}
        sx={{
          width: { xs: '100%', md: 300 },
          '& .MuiInputBase-root': {
            height: 40
          }
        }}
      />
      <FormControl sx={{ width: { xs: '100%', md: 200 } }}>
        <InputLabel>{t('mailTemplate.campaigns.sortBy')}</InputLabel>
        <Select
          value={sortBy}
          onChange={(e) => onSortChange(e.target.value)}
          label={t('mailTemplate.campaigns.sortBy')}
          size="small"
          sx={{
            height: 40,
            '& .MuiSelect-select': {
              paddingRight: '40px !important'
            }
          }}
        >
          {SORT_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {t(option.label)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <ToggleButtonGroup
        value={viewMode}
        exclusive
        size="small"
        onChange={(_, val) => val && onViewModeChange(val)}
        sx={{
          height: 40,
          '& .MuiToggleButton-root': {
            border: '1px solid',
            borderColor: 'divider',
            color: 'text.secondary',
            px: 1.25,
            '&.Mui-selected': {
              backgroundColor: 'rgba(0, 150, 136, 0.1)',
              color: 'primary.main',
              borderColor: 'primary.main',
              '&:hover': { backgroundColor: 'rgba(0, 150, 136, 0.15)' }
            }
          }
        }}
      >
        <ToggleButton value="cards" aria-label="cards view">
          <Tooltip title={t('mailTemplate.campaigns.view.cards') || 'Cards'} arrow>
            <ViewModuleIcon sx={{ fontSize: 20 }} />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="table" aria-label="table view">
          <Tooltip title={t('mailTemplate.campaigns.view.table') || 'Table'} arrow>
            <TableRowsIcon sx={{ fontSize: 20 }} />
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  </Box>
)

const CampaignList = ({
  campaigns,
  campaignReports,
  fetchCampaigns,
  onCreateCampaign,
  refreshCampaignReport,
  campaignReportsFetching,
  isLoading
}) => {
  const { t } = useTranslation()

  const [sortBy, setSortBy] = useState('newest')
  const [searchTerm, setSearchTerm] = useState('')
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem(VIEW_STORAGE_KEY)
      return saved === 'cards' || saved === 'table' ? saved : 'cards'
    } catch {
      return 'cards'
    }
  })

  const handleViewModeChange = (val) => {
    setViewMode(val)
    try { localStorage.setItem(VIEW_STORAGE_KEY, val) } catch {}
  }

  const filteredCampaigns = useMemo(() =>
    filterAndSortCampaigns(campaigns, searchTerm, sortBy),
  [campaigns, searchTerm, sortBy]
  )

  const ListComponent = viewMode === 'cards' ? CampaignCardList : CampaignTableList

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            gap: 2,
            mb: 3
          }}
        >
          <Typography
            variant="h5"
            component="h1"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              whiteSpace: 'nowrap'
            }}
          >
            {t('mailTemplate.campaigns.campaignsTitle')}
          </Typography>

          <CampaignFilters
            t={t}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            sortBy={sortBy}
            onSortChange={setSortBy}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
          />
        </Box>
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400
        }}>
          <CircularProgress size={60}/>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          gap: 2,
          mb: 3
        }}
      >
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 700,
            color: 'primary.main',
            whiteSpace: 'nowrap'
          }}
        >
          {t('mailTemplate.campaigns.campaignsTitle')}
        </Typography>

        <CampaignFilters
          t={t}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          sortBy={sortBy}
          onSortChange={setSortBy}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
        />
      </Box>
      <ListComponent
        data={filteredCampaigns}
        fetchCampaigns={fetchCampaigns}
        campaignReports={campaignReports}
        refreshCampaignReport={refreshCampaignReport}
        campaignReportsFetching={campaignReportsFetching}
        onCreateCampaign={onCreateCampaign}
      />
    </Box>
  )
}

export default CampaignList
